import React, {
  useMemo, useEffect, useState, useCallback, Fragment, ReactElement,
} from 'react';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Widget from '../../../../components/widget/widget.component';
import Chip from '../../../../components/chip/chip.component';
import ChipLink from '../../../../components/chip-link/chip-link.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import ImageContainer from '../../../../components/image-container/image-container.component';
import RequiredMark from '../../../../components/required-mark/required-mark.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../../components/form-field-number/form-field-number.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldMultiSelect from '../../../../components/form-field-multi-select/form-field-multi-select.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import FormatService from '../../../../services/format-service';
import FormService from '../../../../services/form-service';
import { ROUTE_USER } from '../../../../constants/system/routes';
import { CONTACT_VIEW, SETTINGS_USER_LIST_VIEW, SETTINGS_USER_VIEW } from '../../../../constants/entity/permissions';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../../constants/entity/contact-types';
import CompanyDefaultImage from '../../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const ContactWidgetDetails: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Details';
  const WIDGET_NAME: string = 'widget-contact-details';
  const CONTACT_VISUAL_TITLE_COMPANY: string = 'Organization';
  const CONTACT_VISUAL_TITLE_PERSON: string = 'Person';

  const {
    isEditMode,
    isContactLinkedToUser,
    isContactPhotoDeleted,
    formPrefix,
    contactData,
    contactType,
    contactUserData,
    contactPhotoFile,
    contactPhotoPreviewUrl,
    currentUser,
    contactAssetTypes,
    assetTypeList,
    contactTypeList,
    contactCompanyList,
    notLinkedUserList,
    contactGroupList,
    onUserChange,
    onAssetTypesChange,
    onNotLinkedUserSearch,
    onNotLinkedUserSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onContactTypeChange,
    onContactGroupsChange,
    onContactCompanyClick,
    onContactPhotoFileChange,
    onContactPhotoFileDelete,
    onEditModeButtonClick,
  } = props;

  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType[] | null, Function] = useState(null);
  const [selectedContactGroups, setSelectedContactGroups]: [ContactGroup[] | null, Function] = useState(null);

  const settingsUserListViewPermissions: string[] = useMemo((): string[] => [SETTINGS_USER_LIST_VIEW], []);

  const isPersonContact: boolean = contactType === CONTACT_TYPE_PERSON;
  const contactVisualTitle: string = isPersonContact ? CONTACT_VISUAL_TITLE_PERSON : CONTACT_VISUAL_TITLE_COMPANY;
  const widgetTitle: string = `${contactVisualTitle} ${WIDGET_TITLE_DEFAULT}`;
  const withIsDefaultField: boolean = currentUser && currentUser.isPlatformAdmin;
  const withDescriptionField: boolean = (isEditMode || !!contactData.description);
  const withLinkedUserField: boolean = (isEditMode && isPersonContact) || !!contactData.user;
  const filterContactType: string = isEditMode ? contactType : (contactData && contactData.type);
  const isEmpty: boolean = !contactData;

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemContactGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactGroupList,
    selectedItemList: selectedContactGroups,
  }), [contactGroupList, selectedContactGroups]);

  const menuItemNotLinkedUserList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: notLinkedUserList,
    defaultImage: PersonDefaultImage,
  }), [notLinkedUserList]);

  const menuItemContactTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactTypeList,
  }), [contactTypeList]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: contactCompanyList,
    optionItemImageKey: 'photo',
    optionItemImageThumbnailKey: 'photoThumbnail',
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, []);

  const contactGroupsChange: Function = useCallback((contactGroupValues: ContactGroup[]): void => {
    if (onContactGroupsChange) {
      onContactGroupsChange(contactGroupValues);
    }

    setSelectedContactGroups(contactGroupValues);
  }, []);

  const changeContactType: Function = useCallback((contactTypeName: ContactType): void => {
    if (onContactTypeChange) {
      onContactTypeChange(contactTypeName);
    }
  }, []);

  const contactCompanyClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactItemId: number,
  ): void => {
    if (onContactCompanyClick) {
      onContactCompanyClick(event, contactItemId);
    }
  }, []);

  const changeUser: Function = useCallback((userId: number): void => {
    if (onUserChange) {
      onUserChange(userId);
    }
  }, []);

  useEffect((): void => {
    if (contactData) {
      if ((!contactType || contactType !== contactData.type) && onContactTypeChange) {
        onContactTypeChange(contactData.type);
      }

      if (!selectedAssetTypes) {
        setSelectedAssetTypes(contactData.assetTypes || []);
      }

      if (!selectedContactGroups) {
        setSelectedContactGroups(contactData.groups || []);
      }
    }
  }, [contactData, isEditMode]);

  useEffect((): void => {
    if (contactAssetTypes) {
      setSelectedAssetTypes(contactAssetTypes);
    }
  }, [contactAssetTypes]);

  return (
    <Widget
      isFullWidth
      isFullHeight
      withEditMode
      isEmpty={isEmpty}
      isEditMode={isEditMode}
      widgetName={WIDGET_NAME}
      defaultWidgetTitle={widgetTitle}
      onEditModeButtonClick={onEditModeButtonClick}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          {
            (isEditMode || (contactUserData ? (contactUserData.photo || contactUserData.photoThumbnail)
              : (contactData.photo || contactData.photoThumbnail))) && (
              <TableRow>
                <TableCell>
                  Photo
                </TableCell>
                <TableCell>
                  <ImageContainer
                    withNoOffset
                    isDisabled={isContactLinkedToUser}
                    isImageDeleted={isContactPhotoDeleted}
                    isEditMode={isEditMode}
                    image={contactPhotoPreviewUrl || (contactUserData ? contactUserData.photo : contactData.photo)}
                    imageThumbnail={contactUserData ? contactUserData.photoThumbnail : contactData.photoThumbnail}
                    imageFile={contactPhotoFile}
                    imageFormFieldName={`${formPrefix}.photo`}
                    defaultImage={isPersonContact ? PersonDefaultImage : CompanyDefaultImage}
                    onImageFileChange={onContactPhotoFileChange}
                    onImageFileDelete={onContactPhotoFileDelete}
                  />
                  <FormFieldText isHidden name={`${formPrefix}.photoLink`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            withLinkedUserField && (
              <CheckAccess permissions={settingsUserListViewPermissions}>
                <TableRow>
                  <TableCell>
                    Linked User
                  </TableCell>
                  <TableCell>
                    {
                      isEditMode ? (
                        <FormFieldSelect
                          isBordered
                          withSearch
                          withBlankElement
                          isDisabled={notLinkedUserList && !notLinkedUserList.length}
                          searchOptions={{
                            userId: contactData.user ? contactData.user.id : null,
                          }}
                          name={`${formPrefix}.userId`}
                          optionsTotal={notLinkedUserList && notLinkedUserList.length}
                          onSearch={onNotLinkedUserSearch}
                          onClose={onNotLinkedUserSearchClose}
                          onChange={((userId: number): void => {
                            changeUser(userId);
                          })}
                        >
                          {menuItemNotLinkedUserList}
                        </FormFieldSelect>
                      ) : contactData.user && (
                        <FilteredLink
                          withNewWindow
                          link={`${ROUTE_USER}/${contactData.user.id}`}
                          permission={SETTINGS_USER_VIEW}
                        >
                          {contactData.user.fullName}
                        </FilteredLink>
                      )
                    }
                  </TableCell>
                </TableRow>
              </CheckAccess>
            )
          }
          {
            withIsDefaultField && (
              <TableRow>
                <TableCell>
                  Is Default
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldCheckbox name={`${formPrefix}.isDefault`} />
                    ) : FormatService.formatBooleanValue(contactData.isDefault)
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            withDescriptionField && (
              <TableRow>
                <TableCell>
                  Description
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        isMultiline
                        name={`${formPrefix}.description`}
                        maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                        minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                      />
                    ) : contactData.description
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && (
              <TableRow>
                <TableCell>
                  Type
                  {
                    isEditMode && (
                      <RequiredMark />
                    )
                  }
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    isDisabled={!!contactData.id || !!contactData.user
                      || (contactTypeList && !contactTypeList.length)}
                    name={`${formPrefix}.type`}
                    optionsTotal={contactTypeList && contactTypeList.length}
                    onChange={((contactTypeName: string): void => {
                      changeContactType(contactTypeName);
                    })}
                  >
                    {menuItemContactTypeList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || (contactData.groups && !!contactData.groups.length)) && (
              <TableRow>
                <TableCell>
                  Groups
                  {
                    isEditMode && (
                      <RequiredMark />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        isDisabled={contactGroupList && !contactGroupList.length}
                        keyField="name"
                        name={`${formPrefix}.groupIds`}
                        optionList={contactGroupList}
                        onValuesChange={contactGroupsChange}
                      >
                        {menuItemContactGroupList}
                      </FormFieldMultiSelect>
                    ) : (
                      contactData.groups.map((contactGroupItem: ContactGroup): ReactElement => (
                        <Chip
                          isDisabled
                          key={contactGroupItem.id}
                          label={contactGroupItem.title}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (filterContactType === CONTACT_TYPE_COMPANY) && (isEditMode || !!contactData.name) && (
              <TableRow>
                <TableCell>
                  Name
                  {
                    isEditMode && (
                      <RequiredMark />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText
                        isBordered
                        isDisabled={!!contactData.user}
                        name={`${formPrefix}.name`}
                      />
                    ) : contactData.name
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (filterContactType === CONTACT_TYPE_PERSON) && (
              <Fragment>
                {
                  (isEditMode || !!contactData.firstName) && (
                    <TableRow>
                      <TableCell>
                        First Name
                        {
                          isEditMode && (
                            <RequiredMark />
                          )
                        }
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldText
                              isBordered
                              isDisabled={!!contactData.user}
                              name={`${formPrefix}.firstName`}
                            />
                          ) : contactData.firstName
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  (isEditMode || !!contactData.lastName) && (
                    <TableRow>
                      <TableCell>
                        Last Name
                        {
                          isEditMode && (
                            <RequiredMark />
                          )
                        }
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldText
                              isBordered
                              isDisabled={!!contactData.user}
                              name={`${formPrefix}.lastName`}
                            />
                          ) : contactData.lastName
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  (isEditMode || !!contactData.company) && (
                    <TableRow>
                      <TableCell>
                        Company
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldSelect
                              isBordered
                              withSearch
                              withBlankElement
                              isDisabled={!!contactData.user || (contactCompanyList && !contactCompanyList.length)}
                              name={`${formPrefix}.companyId`}
                              optionsTotal={contactCompanyList && contactCompanyList.length}
                              onSearch={onContactCompanySearch}
                              onClose={onContactCompanySearchClose}
                            >
                              {menuItemContactCompanyList}
                            </FormFieldSelect>
                          ) : (
                            contactData.company && (
                              <ChipLink
                                label={contactData.company.name}
                                imageLink={contactData.company.photoThumbnail || contactData.company.photo
                                  || (isPersonContact ? PersonDefaultImage : CompanyDefaultImage)}
                                permission={CONTACT_VIEW}
                                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                  contactCompanyClick(event, contactData.company.id);
                                }}
                              />
                            )
                          )
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  (isEditMode || !!contactData.title) && (
                    <TableRow>
                      <TableCell>
                        Title
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldText isBordered name={`${formPrefix}.title`} />
                          ) : contactData.title
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
              </Fragment>
            )
          }
          {
            (filterContactType === CONTACT_TYPE_COMPANY) && (
              <Fragment>
                {
                  (isEditMode || !!contactData.industry) && (
                    <TableRow>
                      <TableCell>
                        Industry
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldText isBordered name={`${formPrefix}.industry`} />
                          ) : contactData.industry
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  (isEditMode || !!contactData.foundationYear) && (
                    <TableRow>
                      <TableCell>
                        Foundation Year
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.foundationYear`} />
                          ) : contactData.foundationYear
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  (isEditMode || !!contactData.headQuarter) && (
                    <TableRow>
                      <TableCell>
                        Headquarter
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldText isBordered name={`${formPrefix}.headQuarter`} />
                          ) : contactData.headQuarter
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
                {
                  (isEditMode || !!contactData.keySubsidiaries) && (
                    <TableRow>
                      <TableCell>
                        Key Subsidiaries
                      </TableCell>
                      <TableCell>
                        {
                          isEditMode ? (
                            <FormFieldText isBordered name={`${formPrefix}.keySubsidiaries`} />
                          ) : contactData.keySubsidiaries
                        }
                      </TableCell>
                    </TableRow>
                  )
                }
              </Fragment>
            )
          }
          {
            (isEditMode || (contactData.assetTypes && !!contactData.assetTypes.length)) && (
              <TableRow>
                <TableCell>
                  Asset Focus
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        isDisabled={assetTypeList && !assetTypeList.length}
                        keyField="name"
                        name={`${formPrefix}.assetTypes`}
                        optionList={assetTypeList}
                        onValuesChange={assetTypesChange}
                      >
                        {menuItemAssetTypeList}
                      </FormFieldMultiSelect>
                    ) : (
                      contactData.assetTypes.map((assetTypeItem: AssetType): ReactElement => (
                        <Chip
                          isDisabled
                          key={assetTypeItem.id}
                          label={assetTypeItem.title}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </Widget>
  );
});

export default ContactWidgetDetails;
